/**
 * components/landing/platform/sections/Standards.jsx — §3.9.
 *
 * Enterprise standards grid.  Ten trust pillars — multi-tenancy, SSO,
 * RBAC, encryption, backups, audit logs, HA, performance, API,
 * updates.  Presented as a compact, scannable grid.
 */

import React from 'react'
import {
  Layers, Lock, ShieldCheck, KeyRound, DatabaseBackup, FileClock,
  Activity, Gauge, Network, RefreshCw, Sparkles,
} from 'lucide-react'
import { Container, Eyebrow, SectionHeading } from '@/components/landing/design-system'
import { STANDARDS } from '../data'

const ICONS = { Layers, Lock, ShieldCheck, KeyRound, DatabaseBackup, FileClock, Activity, Gauge, Network, RefreshCw }
const TONE = {
  orange: { hex: '#FFC107', rgb: '243,112,33' },
  green:  { hex: '#22c55e', rgb: '34,197,94' },
  blue:   { hex: '#3b82f6', rgb: '59,130,246' },
  purple: { hex: '#a855f7', rgb: '168,85,247' },
  cyan:   { hex: '#22d3ee', rgb: '34,211,238' },
}

export default function Standards() {
  return (
    <section className="relative py-24 md:py-28">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
      <Container size="wide" className="flex flex-col gap-10">
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow>Enterprise Standards</Eyebrow>
          <SectionHeading
            align="center"
            title={<>Built to <span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">pass procurement.</span></>}
            subtitle="Ten default pillars every Kalki Ai Cloud tenant inherits — no separate SKU, no add-on. We’re transparent about what we’ve done and what’s in progress."
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {STANDARDS.map((s) => {
            const tone = TONE[s.tone] || TONE.orange
            const Icon = ICONS[s.iconKey] || Sparkles
            return (
              <div key={s.id} className="relative rounded-2xl border border-white/10 bg-white/[0.02] p-4 backdrop-blur-xl overflow-hidden transition-all hover:border-white/20 hover:bg-white/[0.03] hover:-translate-y-[1px]">
                <div className="pointer-events-none absolute inset-0 opacity-40"
                  style={{ background: `radial-gradient(80% 40% at 20% 0%, rgba(${tone.rgb},0.10), transparent 70%)` }} />
                <div className="relative flex flex-col gap-2.5">
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg border"
                    style={{ borderColor: `rgba(${tone.rgb},0.35)`, background: `rgba(${tone.rgb},0.08)`, color: tone.hex }}>
                    <Icon style={{ width: 15, height: 15 }} strokeWidth={1.8} />
                  </span>
                  <div>
                    <h4 className="text-[13.5px] font-semibold text-white leading-tight">{s.label}</h4>
                    <p className="text-[12px] text-zinc-400 leading-relaxed mt-1">{s.body}</p>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </Container>
    </section>
  )
}
